import React from 'react';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  CartesianGrid,
  Tooltip,
  LineChart as RechartsLineChart,
  Line,
  Legend,
  Sankey
} from 'recharts';

export const CHART_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

const COMMON_MARGIN = { top: 5, right: 5, left: 35, bottom: 25 };

const CommonTooltip = ({ contentStyle = {} }) => (
  <Tooltip
    cursor={{ fill: '#f1f5f9', radius: 8 }}
    contentStyle={{
      borderRadius: '12px',
      border: 'none',
      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
      fontSize: '12px',
      ...contentStyle
    }}
  />
);

const CommonLegend = ({ onClick }) => (
  <Legend
    verticalAlign="top"
    align="right"
    onClick={onClick}
    iconType="circle"
    wrapperStyle={{
      fontSize: '9px',
      fontWeight: 'bold',
      paddingBottom: '8px',
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    }}
  />
);

export const BarTemplate = ({ data, series, xAxis, yAxis, hiddenSeries, handleLegendClick }) => (
  <BarChart data={data} margin={COMMON_MARGIN}>
    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
    {xAxis}
    {yAxis}
    <CommonTooltip />
    <CommonLegend onClick={handleLegendClick} />
    {series.map((s, i) => (
      <Bar
        key={s}
        dataKey={s}
        fill={CHART_COLORS[i % CHART_COLORS.length]}
        radius={[4, 4, 0, 0]}
        hide={hiddenSeries.has(s)}
        animationDuration={500}
      />
    ))}
  </BarChart>
);

export const LineTemplate = ({ data, series, xAxis, yAxis, hiddenSeries, handleLegendClick }) => (
  <RechartsLineChart data={data} margin={COMMON_MARGIN}>
    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
    {xAxis}
    {yAxis}
    <CommonTooltip />
    <CommonLegend onClick={handleLegendClick} />
    {series.map((s, i) => (
      <Line
        key={s}
        type="monotone"
        dataKey={s}
        stroke={CHART_COLORS[i % CHART_COLORS.length]}
        strokeWidth={2.5}
        dot={{ r: 3, strokeWidth: 2, fill: '#fff' }}
        activeDot={{ r: 5, strokeWidth: 0 }}
        hide={hiddenSeries.has(s)}
        animationDuration={500}
      />
    ))}
  </RechartsLineChart>
);

export const ScatterTemplate = ({ data, series, xAxis, yAxis, hiddenSeries, handleLegendClick }) => (
  <RechartsLineChart data={data} margin={COMMON_MARGIN}>
    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
    {xAxis}
    {yAxis}
    <CommonTooltip cursor={{ strokeDasharray: '3 3' }} />
    <CommonLegend onClick={handleLegendClick} />
    {series.map((s, i) => (
      <Line
        key={s}
        line={false}
        dataKey={s}
        stroke={CHART_COLORS[i % CHART_COLORS.length]}
        strokeWidth={0}
        dot={{ r: 4, fill: CHART_COLORS[i % CHART_COLORS.length] }}
        hide={hiddenSeries.has(s)}
        animationDuration={500}
      />
    ))}
  </RechartsLineChart>
);
export const HistogramTemplate = ({ data, series, xAxis, yAxis, hiddenSeries, handleLegendClick }) => {
  const isComparison = series.length > 1;

  // Comparison histogram: overlapping area curves for distribution overlay
  if (isComparison) {
    return (
      <AreaChart data={data} margin={COMMON_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
        {xAxis}
        {yAxis}
        <CommonTooltip />
        <CommonLegend onClick={handleLegendClick} />
        {series.map((s, i) => (
          <Area
            key={s}
            type="monotone"
            dataKey={s}
            stroke={CHART_COLORS[i % CHART_COLORS.length]}
            fill={CHART_COLORS[i % CHART_COLORS.length]}
            fillOpacity={0.25}
            strokeWidth={2}
            hide={hiddenSeries.has(s)}
            animationDuration={500}
          />
        ))}
      </AreaChart>
    );
  }

  // Single histogram: continuous bars with no gaps and flat tops
  return (
    <BarChart data={data} margin={COMMON_MARGIN} barGap={0} barCategoryGap={0}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
      {xAxis}
      {yAxis}
      <CommonTooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.4 }} />
      {series.map((s, i) => (
        <Bar
          key={s}
          dataKey={s}
          fill={CHART_COLORS[i % CHART_COLORS.length]}
          stroke={CHART_COLORS[i % CHART_COLORS.length]}
          strokeWidth={0.5}
          radius={0}
          hide={hiddenSeries.has(s)}
          animationDuration={500}
        />
      ))}
    </BarChart>
  );
};


// Minimal custom node — only adds a label next to the default recharts rect
const SankeyNodeWithLabel = ({ x, y, width, height, index, payload }) => {
  const nodeName = payload?.name || `Node ${index}`;
  const isLeft = x < 300;

  const safeW = Math.max(0.5, width || 0);
  const safeH = Math.max(0.5, height || 0);
  const color = CHART_COLORS[index % CHART_COLORS.length];

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={safeW}
        height={safeH}
        fill={color}
        fillOpacity={0.9}
        rx={3}
      />
      <text
        x={isLeft ? x - 8 : x + safeW + 8}
        y={y + safeH / 2}
        textAnchor={isLeft ? 'end' : 'start'}
        dominantBaseline="middle"
        fontSize={11}
        fontWeight={600}
        fill="#334155"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {nodeName}
      </text>
    </g>
  );
};

export const SankeyTemplate = ({ data }) => {
  return (
    <Sankey
      data={data}
      nodeWidth={10}
      nodePadding={25}
      linkCurvature={0.5}
      margin={{ top: 20, right: 150, left: 150, bottom: 20 }}
      node={<SankeyNodeWithLabel />}
      link={{ stroke: '#94a3b8' }}
      sort
      iterations={64}
      nameKey="name"
    >
      <CommonTooltip />
    </Sankey>
  );
};
